/**
 * Metric Card Component
 *
 * Compact metric display card for third-party analytics.
 * Similar to KPICard but optimized for the analytics pages.
 */

import { useState } from 'react';
import {
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';

// Format value based on type
function formatValue(value, format) {
  if (value === null || value === undefined) return 'N/A';

  // Convert to number if string (backend may return strings)
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(numValue)) return 'N/A';

  switch (format) {
    case 'currency':
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(numValue);
    case 'percent':
      // If value is between 0 and 1, multiply by 100
      const pct = numValue <= 1 && numValue >= 0 ? numValue * 100 : numValue;
      return `${pct.toFixed(1)}%`;
    case 'decimal':
      return numValue.toFixed(2);
    case 'bytes':
      // Convert bytes to appropriate unit
      if (numValue >= 1073741824) return `${(numValue / 1073741824).toFixed(2)} GB`;
      if (numValue >= 1048576) return `${(numValue / 1048576).toFixed(2)} MB`;
      if (numValue >= 1024) return `${(numValue / 1024).toFixed(2)} KB`;
      return `${numValue} B`;
    case 'gb':
      return `${numValue.toFixed(2)} GB`;
    case 'ms':
      return `${numValue.toFixed(0)}ms`;
    default:
      return typeof numValue === 'number'
        ? new Intl.NumberFormat('en-US').format(Math.round(numValue))
        : value;
  }
}

export default function MetricCard({
  title,
  value,
  change,
  format = 'number',
  icon: Icon,
  status, // 'healthy', 'warning', 'critical'
  subtitle,
  description, // Tooltip text shown on hover
  loading = false,
}) {
  const [showTooltip, setShowTooltip] = useState(false);
  const formattedValue = formatValue(value, format);

  // Change is in decimal form (0.15 = 15%)
  const changePercent = change !== undefined ? Math.abs(change * 100).toFixed(1) : null;
  const isPositive = change >= 0;
  const changeColor = isPositive ? 'text-green-600' : 'text-red-600';
  const ChangeTrendIcon = isPositive ? ArrowTrendingUpIcon : ArrowTrendingDownIcon;

  const statusColors = {
    healthy: 'border-l-green-500',
    warning: 'border-l-yellow-500',
    critical: 'border-l-red-500',
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 animate-pulse">
        <div className="h-4 bg-gray-200 rounded w-1/2 mb-3"></div>
        <div className="h-8 bg-gray-200 rounded w-3/4 mb-2"></div>
        <div className="h-3 bg-gray-200 rounded w-1/3"></div>
      </div>
    );
  }

  return (
    <div
      className={`bg-white rounded-xl shadow-sm border border-gray-100 p-5 hover:shadow-md transition-shadow ${
        status ? `border-l-4 ${statusColors[status]}` : ''
      }`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1">
            <p className="text-sm text-gray-500 font-medium truncate">
              {title}
            </p>
            {description && (
              <div className="relative">
                <InformationCircleIcon
                  className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help"
                  onMouseEnter={() => setShowTooltip(true)}
                  onMouseLeave={() => setShowTooltip(false)}
                />
                {showTooltip && (
                  <div className="absolute z-10 bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 text-xs text-white bg-gray-900 rounded-lg shadow-lg w-48 text-center">
                    {description}
                    <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900"></div>
                  </div>
                )}
              </div>
            )}
          </div>
          <p className="text-2xl font-bold mt-1 text-gray-900">
            {formattedValue}
          </p>
          {subtitle && (
            <p className="text-xs text-gray-400 mt-1">{subtitle}</p>
          )}
          {changePercent !== null && (
            <div className={`flex items-center gap-1 mt-2 ${changeColor}`}>
              <ChangeTrendIcon className="w-4 h-4" />
              <span className="text-sm font-medium">
                {changePercent}%
              </span>
            </div>
          )}
        </div>
        {Icon && (
          <div className="p-2 bg-blue-50 rounded-lg flex-shrink-0 ml-3">
            <Icon className="w-5 h-5 text-blue-600" />
          </div>
        )}
      </div>
    </div>
  );
}
